import { Button } from '@/components/Atoms/Button/Button';
import { Hidden } from '@/components/Atoms/Hidden/Hidden';
import { Icon, IconSize } from '@/components/Atoms/Icon/Icon';
import { MarkedText } from '@/components/Atoms/MarkedText/MarkedText';
import { TimeAgo } from '@/components/Atoms/TimeAgo/TimeAgo';
import ChevronLeft from '@/components/Icons/ChevronLeft';
import ChevronRight from '@/components/Icons/ChevronRight';
import type { IResultListItem } from '@/components/Templates/InfiniteScrollPage/InfiniteScrollPage';
import { useMastheadSize } from '@/hooks/useMastheadSize';
import useTranslation from '@/hooks/useTranslation';
import { trackEvent } from '@/services/Analytics/BBAnalytics';
import { TrackingAction, TrackingCategory } from '@/services/Analytics/bnefAnalyticsSettings';
import classNames from '@/utils/classNames';
import { useEffect, useRef } from 'react';
import styles from './ClothesLine.module.css';

const ITEM_WIDTH = 280;

const getScrollDistance = (): number => {
    if (window.innerWidth < ITEM_WIDTH * 3) {
        return ITEM_WIDTH;
    }
    return ITEM_WIDTH * 2;
};

export const ClothesLine = ({ searchResults, onChangePosition, activePosition, progress }: IProps) => {
    const { t } = useTranslation('common');
    const scrollRef = useRef<HTMLDivElement>(null);
    const outerRef = useRef<HTMLDivElement>(null);
    useMastheadSize('clothesline', outerRef);

    useEffect(() => {
        scrollRef.current.scrollLeft = ITEM_WIDTH * activePosition;
    }, [activePosition]);

    return (
        <div className={styles.outerWrap} ref={outerRef}>
            <div className={styles.innerWrap} ref={scrollRef}>
                <ul className={styles.list}>
                    {searchResults.map((v: IResultListItem, i: number) => (
                        <li key={v.item.id}>
                            <a
                                href={v.item.url}
                                className={classNames({
                                    [styles.link]: i !== activePosition,
                                    [styles.linkActive]: i === activePosition,
                                })}
                                onClick={(e) => {
                                    e.preventDefault();
                                    trackEvent(TrackingAction.Click, TrackingCategory.ClothesLine, null, i);
                                    onChangePosition(i);
                                }}
                            >
                                <h4 className={classNames(styles.title, 'cds-t-body cds-t-body--b')}>
                                    <MarkedText value={v.item.title} />
                                </h4>
                                <TimeAgo className="cds-t-body-small" datetime={v.item.displayDate} />
                                {i === activePosition && (
                                    <div className={styles.progress} style={{ width: `${progress}%` }} />
                                )}
                            </a>
                        </li>
                    ))}
                </ul>
            </div>
            <div className={styles.arrowWrap}>
                <Button
                    className={styles.scrollArrow}
                    onClick={() => (scrollRef.current.scrollLeft -= getScrollDistance())}
                >
                    <Icon use={<ChevronLeft />} size={IconSize.STANDARD} />
                    <Hidden>{t('CarouselControls.previous')}</Hidden>
                </Button>
                <Button
                    className={styles.scrollArrow}
                    onClick={() => (scrollRef.current.scrollLeft += getScrollDistance())}
                >
                    <Icon use={<ChevronRight />} size={IconSize.STANDARD} />
                    <Hidden>{t('CarouselControls.next')}</Hidden>
                </Button>
            </div>
        </div>
    );
};

interface IProps {
    searchResults: IResultListItem[];
    activePosition: number;
    progress: number;
    onChangePosition: (newPosition: number) => void;
}
